// GeneralSettingsPage.js - Main Settings Page Component using existing APICall and AuthProvider
import React, { useState } from "react";
import {
  AppSettings,
  useSettings,
  useUpdateSettings,
} from "../../Contexts/SettingsContext";
import { useAuth } from "../../Utility/AuthProvider";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants"; // Your existing paths

// Reusable Toggle Switch Component
const ToggleSwitch = ({ isActive, onToggle, disabled = false }) => {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        isActive ? "bg-blue-600" : "bg-gray-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          isActive ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
};

// Label Switch Component
const LabelSwitch = ({
  label,
  description,
  isActive,
  onValueChange,
  disabled = false,
}) => {
  return (
    <div className="flex items-start justify-between py-4 border-b border-gray-100 last:border-b-0">
      <div className="flex-1 mr-4">
        <h3 className="text-base font-medium text-gray-900 mb-1">{label}</h3>
        {description && (
          <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="flex-shrink-0">
        <ToggleSwitch
          isActive={isActive}
          onToggle={onValueChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

// Dropdown Component
const Dropdown = ({ value, onChange, options, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="mb-6">
      {label && (
        <label className="block text-base font-medium text-gray-900 mb-3">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-left text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
        >
          <span>{selectedOption ? selectedOption.label : placeholder}</span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none first:rounded-t-lg last:rounded-b-lg"
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Loading Overlay Component
const LoadingOverlay = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="text-gray-900">Saving settings...</span>
      </div>
    </div>
  );
};

// Toast Notification Component
const ToastNotification = ({ isVisible, type = "error", description }) => {
  if (!isVisible) return null;

  const bgColor = type === "error" ? "bg-red-500" : "bg-green-500";

  return (
    <div className="fixed top-4 right-4 z-50">
      <div
        className={`${bgColor} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}
      >
        {type === "error" ? (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
        <span>{description}</span>
      </div>
    </div>
  );
};

// Main Settings Page Component
const GeneralSettingsPage = () => {
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const { jwt } = useAuth(); // Use your existing auth provider
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const getSettingsValue = (property) => {
    return settings?.[property] === undefined
      ? AppSettings.getDefaultValue(property)
      : settings[property];
  };

  const handleUpdateSettings = async (property) => {
    const newValue = !getSettingsValue(property);
    const newSettings = {
      ...settings,
      [property]: newValue,
    };

    updateSettings({ [property]: newValue });
    await syncSettingsToDatabase(newSettings);
  };

  const syncSettingsToDatabase = async (newSettings) => {
    try {
      setIsSettingsLoading(true);

      let settingsToSend = {};
      AppSettings.getValuesToStoreInDB().forEach((key) => {
        settingsToSend[key] =
          newSettings?.[key] ?? AppSettings.getDefaultValue(key);
      });

      const body = {
        settings: JSON.stringify(settingsToSend),
      };

      // Use your existing APICall function
      const response = await APICall(
        "PUT",
        paths.user.update,
        body,
        undefined,
        jwt
      );

      // Handle response based on your API response structure
      if (response && response.status === "success") {
        console.log("Settings updated successfully");
      } else {
        throw new Error(response?.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error syncing settings to database:", error);
      setErrorMessage("Failed to save settings. Please try again.");
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsSettingsLoading(false);
    }
  };

  const handleUpdateDefaultLanguage = async (language) => {
    const newSettings = {
      ...settings,
      language: language.value ?? AppSettings.getDefaultValue("language"),
    };

    updateSettings({ language: language.value });
    await syncSettingsToDatabase(newSettings);
  };

  const languageData = [
    { label: "English", value: "english" },
    // Add more languages as needed
    // { label: 'Hindi', value: 'hindi' },
  ];

  if (!settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">
            Manage your app preferences and configurations
          </p>
        </div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {/* App Behavior Settings */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                App Behavior
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Configure how the app behaves
              </p>
            </div>
            <div className="p-6 space-y-0">
              {/* <LabelSwitch
                label="Haptic Feedback"
                isActive={getSettingsValue(AppSettings.ALLOW_HAPTIC_FEEDBACK)}
                onValueChange={() =>
                  handleUpdateSettings(AppSettings.ALLOW_HAPTIC_FEEDBACK)
                }
              /> */}

              <LabelSwitch
                label="Remind to WhatsApp"
                description="Reminder automatically opens WhatsApp. Disable to open share sheet."
                isActive={getSettingsValue(
                  AppSettings.OPEN_REMINDER_IN_WHATSAPP
                )}
                onValueChange={() =>
                  handleUpdateSettings(AppSettings.OPEN_REMINDER_IN_WHATSAPP)
                }
              />

              <LabelSwitch
                label="Tab Names"
                description="Whether to show the names of the tabs in the tab bar."
                isActive={getSettingsValue(AppSettings.SHOW_TAB_NAMES)}
                onValueChange={() =>
                  handleUpdateSettings(AppSettings.SHOW_TAB_NAMES)
                }
              />
            </div>
          </div>

          {/* Reports Settings */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
              <p className="text-sm text-gray-500 mt-1">
                Customize how reports are displayed
              </p>
            </div>
            <div className="p-6 space-y-0">
              <LabelSwitch
                label="Future Reports"
                description="Show Reports for next month as well."
                isActive={getSettingsValue(AppSettings.SHOW_FUTURE_REPORTS)}
                onValueChange={() =>
                  handleUpdateSettings(AppSettings.SHOW_FUTURE_REPORTS)
                }
              />

              <LabelSwitch
                label="Sort Reports by Latest"
                description="Show Reports for the current (or next) month at the top."
                isActive={getSettingsValue(
                  AppSettings.SORT_REPORTS_BY_LATEST_FIRST
                )}
                onValueChange={() =>
                  handleUpdateSettings(AppSettings.SORT_REPORTS_BY_LATEST_FIRST)
                }
              />
            </div>
          </div>

          {/* Language Settings */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Language & Region
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Set your preferred language
              </p>
            </div>
            <div className="p-6">
              <Dropdown
                label="App Language"
                value={
                  settings.language ?? AppSettings.getDefaultValue("language")
                }
                onChange={handleUpdateDefaultLanguage}
                options={languageData}
                placeholder="Select Default Language"
              />
              <p className="text-sm text-gray-500">
                The default language of the app wherever applicable
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      <LoadingOverlay isLoading={isSettingsLoading} />

      {/* Error Toast */}
      <ToastNotification
        isVisible={showError}
        type="error"
        description={errorMessage}
      />
    </>
  );
};

export default GeneralSettingsPage;
